/**
 * NewsletterAutoDraftModal — Phase 4 Mode B (Claude generates from scratch).
 *
 * Generation takes ~30-60s, so we use the background-job pattern:
 *   1. POST /auto-draft → returns { job_id }
 *   2. Poll GET /auto-draft/jobs/{job_id} every 3s
 *   3. On done: navigate to the editor for the new newsletter
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { X, Sparkles, Loader2, AlertTriangle } from 'lucide-react';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];


export default function NewsletterAutoDraftModal({ clientId, onClose }) {
  const navigate = useNavigate();
  const today = new Date();
  const next = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const [month, setMonth] = useState(next.getMonth() + 1);
  const [year, setYear] = useState(next.getFullYear());
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);   // 'running' | 'done' | 'failed' | 'conflict'
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');

  const start = async () => {
    setBusy(true);
    setStatus('running');
    setError(null);
    setProgress('Calling Claude…');
    let jobId;
    try {
      const r = await api.post('/newsletters/auto-draft',
        { client_id: clientId, month: Number(month), year: Number(year) });
      jobId = r.data.job_id;
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setStatus('failed');
      setBusy(false);
      return;
    }

    // Poll every 3 seconds, up to 4 minutes.
    const started = Date.now();
    while (Date.now() - started < 240000) {
      await new Promise((res) => setTimeout(res, 3000));
      try {
        const job = await api.get(`/newsletters/auto-draft/jobs/${jobId}`);
        const s = job.data.status;
        setProgress(`Job status: ${s}${s === 'running' ? ' · this can take ~45-60s' : ''}`);
        if (s === 'done') {
          setStatus('done');
          setBusy(false);
          navigate(`/newsletters/${job.data.newsletter_id}`);
          onClose?.();
          return;
        }
        if (s === 'failed') {
          setStatus('failed');
          setError(job.data.error || 'Auto-draft failed.');
          setBusy(false);
          return;
        }
        if (s === 'conflict') {
          setStatus('conflict');
          setError(job.data.error || 'Newsletter for this period already exists.');
          setBusy(false);
          return;
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
        setStatus('failed');
        setBusy(false);
        return;
      }
    }
    setError('Timed out waiting for Claude (>4 min). Try again or contact support.');
    setStatus('failed');
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="newsletter-autodraft-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E5E1DC' }}>
          <h2 className="font-semibold text-[15px] flex items-center gap-2" style={{ color: '#1B2A4A' }}>
            <Sparkles size={15} style={{ color: '#7A5C2B' }} /> Auto-draft with Claude
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"
            data-testid="newsletter-autodraft-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-[12px]" style={{ color: '#6B7280' }}>
            Claude generates a complete starter newsletter for the chosen month — five sections,
            12 items, real venues + real festival dates. You then edit, add images, and approve.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Month</label>
              <select value={month} onChange={(e) => setMonth(e.target.value)} disabled={busy}
                className="w-full mt-1 px-2 py-1.5 border rounded text-[13px]"
                style={{ borderColor: '#E5E1DC' }}
                data-testid="autodraft-month">
                {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Year</label>
              <input type="number" value={year} onChange={(e) => setYear(e.target.value)} disabled={busy}
                className="w-full mt-1 px-2 py-1.5 border rounded text-[13px]"
                style={{ borderColor: '#E5E1DC' }}
                data-testid="autodraft-year" />
            </div>
          </div>

          <div className="text-[11px]" style={{ color: '#6B7280' }}>
            Client: <strong>{clientId.toUpperCase()}</strong>
          </div>

          {status === 'running' && (
            <div className="text-[12px] flex items-center gap-2 p-3 rounded-md"
              style={{ background: '#FFF8EC', color: '#7A5C2B' }}
              data-testid="newsletter-autodraft-progress">
              <Loader2 size={13} className="animate-spin" />
              {progress || 'Generating…'}
            </div>
          )}

          {error && (
            <div className="text-[12px] px-3 py-2 rounded-md flex items-start gap-2"
              style={{ background: '#FEE2E2', color: '#7a1f1f' }}
              data-testid="newsletter-autodraft-error">
              <AlertTriangle size={13} className="mt-0.5" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t" style={{ borderColor: '#E5E1DC' }}>
            <button onClick={onClose} disabled={busy}
              className="text-[12px] px-3 py-1.5 rounded-md border hover:bg-gray-50"
              style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
              Cancel
            </button>
            <button onClick={start} disabled={busy}
              className="text-[12px] px-3 py-1.5 rounded-md text-white inline-flex items-center gap-1"
              style={{ backgroundColor: '#7A5C2B', opacity: busy ? 0.6 : 1 }}
              data-testid="newsletter-autodraft-submit">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {busy ? 'Generating…' : 'Generate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
